/* Copyright (c) 2026 Health Endeavors, LLC. All Rights Reserved */

import { IObservation_ReferenceRange } from '@ahryman40k/ts-fhir-types/lib/R4';
import { WideTooltip } from '../chat/components/WideTooltip';
import { formatDateTime } from '../utils';
import { IObservationSimplified, ObservationValueInterpretationEnum } from './fhir_types';
import { getTextReferenceRangeInterpretation } from './fhir_utils';

import AboveNormalRange from './above normal.svg';
import BelowNormalRange from './below_normal.svg';
import styles from './FhirWidget.module.scss';

function getReferenceRangeText(referenceRange: IObservation_ReferenceRange[] | undefined) {
  //return referenceRange?.length && referenceRange?.length > 0 ? <span className={styles.rangeText}>Ref</span> : null;
  return null;
}

/** Return a 2-tuple with the first value being the observation value as a JSX element, and the second being an
 *  image to show if the value is above or below the reference range.
 */
export function getObservationValue({
  observation,
}: {
  observation: IObservationSimplified | undefined;
}): [JSX.Element | null, JSX.Element | null] {
  if (!observation) return [null, null];
  // console.log('found', observation.code.text, observation);

  if (observation.valueQuantity) {
    const value = observation.valueQuantity?.value;
    // Note, don't use unit here -
    if (value == null) {
      return [null, null];
    }
    const valueStr = `${observation.valueQuantity?.comparator || ''}${value}`;
    let foundRange = false;
    for (let i = 0; observation.referenceRange && i < observation.referenceRange?.length; i++) {
      const range = observation.referenceRange[i];
      if (!range?.low && !range?.high && range?.text) {
        const textInterpretation = getTextReferenceRangeInterpretation(range.text, value);
        if (textInterpretation) {
          foundRange = true;
          if (textInterpretation === ObservationValueInterpretationEnum.LOW) {
            return [
              <span className={styles.belowRange}>{valueStr}</span>,
              <img alt="Below Range" src={BelowNormalRange} />,
            ];
          }
          if (textInterpretation === ObservationValueInterpretationEnum.HIGH) {
            return [
              <span className={styles.aboveRange}>{valueStr}</span>,
              <img alt="Above Range" src={AboveNormalRange} />,
            ];
          }
        }
      }
      if (range?.low?.value != null) {
        foundRange = true;
        if (value < range?.low?.value) {
          return [
            <span className={styles.belowRange}>{valueStr}</span>,
            <img alt="Below Range" src={BelowNormalRange} />,
          ];
        }
      }
      if (range?.high?.value != null) {
        foundRange = true;
        if (value > range?.high?.value) {
          return [
            <span className={styles.aboveRange}>{valueStr}</span>,
            <img alt="Above Range" src={AboveNormalRange} />,
          ];
        }
      }
    }
    if (foundRange) {
      return [<span className={styles.inRange}>{valueStr}</span>, getReferenceRangeText(observation.referenceRange)];
    }
    return [<span className={styles.noRange}>{valueStr}</span>, getReferenceRangeText(observation.referenceRange)];
  } else if (observation.valueCodeableConcept?.text) {
    const snomed = observation.valueCodeableConcept!.coding?.find((c) => c.system === 'http://snomed.info/sct');
    if (snomed?.code) {
      return [
        <a
          target="_blank"
          rel="noreferrer"
          href={`https://www.findacode.com/snomed/${snomed?.code}.html`}
          className={styles.valueString}
        >
          {observation.valueCodeableConcept?.text}
        </a>,
        getReferenceRangeText(observation.referenceRange),
      ];
    } else {
      return [
        <span className={styles.valueString}>{observation.valueCodeableConcept?.text}</span>,
        getReferenceRangeText(observation.referenceRange),
      ];
    }
  }
  if (observation.valueString) {
    if (observation.valueString.length < 50) {
      return [
        <span className={styles.valueString}>{observation.valueString}</span>,
        getReferenceRangeText(observation.referenceRange),
      ];
    } else {
      // Too long to show - make it a tooltip
      return [
        <WideTooltip
          arrow
          title={
            <div style={{ height: '50vh', overflow: 'auto' }}>
              <span className={styles.valueStringEllipse}>{observation.valueString}</span>
            </div>
          }
        >
          <span className={styles.valueStringHoverable}>Hover to view</span>
        </WideTooltip>,
        getReferenceRangeText(observation.referenceRange),
      ];
    }
  }
  if (observation.valueBoolean) {
    return [
      <span>{observation.valueBoolean ? 'positive' : 'negative'}</span>,
      getReferenceRangeText(observation.referenceRange),
    ];
  }
  if (observation.valueDateTime) {
    return [
      <span>{formatDateTime(observation.valueDateTime)}</span>,
      getReferenceRangeText(observation.referenceRange),
    ];
  }
  if (observation.valueRatio) {
    const ratio = observation.valueRatio;
    return [
      <span>
        {ratio.numerator?.comparator}
        {ratio.numerator?.value}:{ratio.denominator?.value}
        {ratio.numerator?.unit}
      </span>,
      getReferenceRangeText(observation.referenceRange),
    ];
  }
  if (observation.dataAbsentReason?.text && !observation.note) {
    // Only show the data absent reason if there's no note
    return [
      <span className={styles.dataAbsent}>
        <i>Data absent reason</i>:<br />
        {observation.dataAbsentReason!.text}
      </span>,
      getReferenceRangeText(observation.referenceRange),
    ];
  }
  return [null, getReferenceRangeText(observation.referenceRange)];
}
